<script setup lang="ts">
/**
 * 合规公示页脚
 *
 * ★ 为什么这个组件必须出现在**登录前**也能看到的地方：
 *
 *   《互联网信息服务算法推荐管理规定》要求算法备案信息对公众可见。
 *   把公示放在登录墙后面**等于没有公示** —— 监管检查时第一眼就是看
 *   未登录状态下能不能看到备案号。所以它同时挂在登录页和应用外壳里。
 *
 *   审计文档 §C6 把 `/public/compliance-notices` 标为「监管必看项」，
 *   并指出当时「实现层没有匿名可达的公示接口」。接口已补上
 *   （`apps/server/src/modules/public/public.controller.ts`），
 *   这个组件是它的展示落点 —— 有接口没人看，等于只做了一半。
 *
 * ★ 数据全部来自公开接口，不含任何敏感信息；请求失败时静默降级为
 *   "只显示静态主体与备案号"，绝不在页面上抛错误（页脚报错比不显示更难看）。
 */
import { onMounted, ref } from 'vue';

interface Notices {
  serviceName: string;
  serviceProvider: string;
  icpLicenseNo: string;
  algorithmFilingNo: string;
  modelFilingInfo: string;
  storyAdDisclaimers: string[];
  complaintChannel: string;
  aiContentLabeling?: { labels: Array<{ key: string; label: string }> };
}

const notices = ref<Notices | null>(null);
const showDetail = ref(false);

onMounted(async () => {
  try {
    const res = await fetch('/api/v1/public/compliance-notices');
    if (!res.ok) return;
    const json = await res.json();
    notices.value = json?.data ?? null;
  } catch {
    /* 公示信息拉不到就只显示静态部分，不打扰用户 */
  }
});
</script>

<template>
  <footer class="cf">
    <div class="cf-line">
      <span class="cf-provider">{{ notices?.serviceProvider ?? '杭州亿货宝企业管理咨询有限公司' }}</span>
      <span class="cf-sep">·</span>
      <a
        class="cf-icp"
        href="https://beian.miit.gov.cn/"
        target="_blank"
        rel="noopener nofollow"
      >{{ notices?.icpLicenseNo ?? '浙ICP备2025195038号-2' }}</a>

      <!-- ★ 算法备案号：监管必看项，非空时必须显著展示 -->
      <template v-if="notices?.algorithmFilingNo">
        <span class="cf-sep">·</span>
        <span class="cf-filing">算法备案 {{ notices.algorithmFilingNo }}</span>
      </template>

      <span class="cf-sep">·</span>
      <button class="cf-more" type="button" @click="showDetail = !showDetail">
        {{ showDetail ? '收起' : 'AI 内容标识公示' }}
      </button>
    </div>

    <!-- 展开后的公示明细 -->
    <div v-if="showDetail" class="cf-detail">
      <p class="cf-p">
        <b>AI 生成内容标识：</b>
        本服务生成的内容均添加显式水印与隐式元数据标识，两者回读校验通过后才允许导出。
        在平台发布时，你仍需主动勾选对应的内容声明（如「含有AI生成内容」「含有营销信息」）。
      </p>

      <p v-if="notices?.aiContentLabeling?.labels?.length" class="cf-p">
        <b>内容标签（网信办规定）：</b>
        {{ notices.aiContentLabeling.labels.map((l) => l.label).join('、') }}
      </p>

      <p v-if="notices?.modelFilingInfo" class="cf-p">
        <b>所用模型：</b>{{ notices.modelFilingInfo }}
      </p>

      <p v-if="notices?.storyAdDisclaimers?.length" class="cf-p">
        <b>剧情类内容告知：</b>{{ notices.storyAdDisclaimers.join('；') }}
      </p>

      <p class="cf-p">
        <b>内容红线：</b>不做多集连续剧集与短剧发行；禁止绝对化用语、禁止承诺功效、禁止贬低同行。
      </p>

      <p v-if="notices?.complaintChannel" class="cf-p">
        <b>投诉与举报：</b>{{ notices.complaintChannel }}
      </p>
    </div>
  </footer>
</template>

<style scoped>
.cf {
  padding: var(--s-4) var(--s-6);
  border-top: 1px solid var(--line);
  font-size: 12px;
  color: var(--text-3);
  background: var(--bg-1, #fff);
}
.cf-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  justify-content: center;
}
.cf-sep {
  opacity: 0.45;
}
/**
 * ★ 可点元素的触控高度必须 ≥24px（WCAG 2.1 SC 2.5.8）。
 *
 *   页脚的链接原来只有 18px 高（纯文字行高撑出来的），
 *   在手机上很难点中。而且这个页脚挂在**每个页面**上，
 *   一处不合格等于全站不合格 —— H5 走查里 10 个页面全部报了这两条。
 *
 *   用 inline-flex + min-height 撑高度，视觉上仍然只是普通文字链接。
 */
.cf-icp,
.cf-more {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 4px;
}
.cf-icp {
  color: var(--text-3);
  text-decoration: none;
}
.cf-icp:hover {
  color: var(--accent, #2563eb);
  text-decoration: underline;
}
.cf-filing {
  color: var(--text-2);
  display: inline-flex;
  align-items: center;
  min-height: 28px;
}
.cf-more {
  border: none;
  background: none;
  font: inherit;
  color: var(--accent, #2563eb);
  cursor: pointer;
  text-decoration: underline;
}
.cf-detail {
  max-width: 880px;
  margin: var(--s-3) auto 0;
  padding-top: var(--s-3);
  border-top: 1px dashed var(--line);
  line-height: var(--lh-relaxed);
}
.cf-p {
  margin: 0 0 6px;
}
.cf-p b {
  color: var(--text-2);
}
</style>
